import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PositionGrid from '../partials/PositionGrid';

import {
  Card, BlockStack, InlineStack, Text, Button, IndexTable, Badge,
  Banner, Box,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import { batchFind } from '../../redux/actions/batches';
import { locationsIndex } from '../../redux/actions/locations';
import { UI_COLORS } from '../../constants';

const STATUS_LABELS = {
  prepared: 'Prepared',
  picking: 'Picking',
  picking_parked: 'Picking (Parked)',
  picked: 'Picked',
  packing: 'Packing',
  packing_parked: 'Packing (Parked)',
  packed_with_returns: 'Packed - Returns',
  to_return: 'To Return',
  returning: 'Returning',
  returning_parked: 'Returning (Parked)',
  complete: 'Complete'
};

const STATUS_BADGE_TONES = {
  prepared: 'info',
  picking: 'warning',
  picking_parked: 'warning',
  picked: 'info',
  packing: 'warning',
  packing_parked: 'warning',
  packed_with_returns: 'warning',
  to_return: 'info',
  returning: 'info',
  returning_parked: 'warning',
  complete: 'success'
};

const STATUS_FIELDS = [
  ['prepared_at', 'Prepared'],
  ['picking_at', 'Picking started'],
  ['picked_at', 'Picked'],
  ['packing_at', 'Packing started'],
  ['packed_at', 'Packed'],
  ['packed_with_returns_at', 'Packed - returns remain'],
  ['to_return_at', 'Marked to return'],
  ['returning_at', 'Returning started'],
  ['returned_at', 'Returned to stock'],
  ['set_aside_at', 'Set aside'],
  ['complete_at', 'Complete'],
];

const formatPark = (park) => `${park.user || 'Unknown user'} at ${formatTime(park.parked_at)}`;

const formatTime = (value) => value ? new Date(value).toLocaleString() : '-';

const formatAddress = (address) => {
  if (!address) return ['No shipping address recorded.'];

  const name = [address.first_name, address.last_name].filter(Boolean).join(' ');
  const lines = [
    name,
    address.company,
    address.street_1,
    address.street_2,
    [address.city, address.state, address.zip].filter(Boolean).join(', '),
    address.country
  ].filter((line) => line && line !== '');

  return lines.length > 0 ? lines : ['No shipping address recorded.'];
};

const BatchDetail = ({ batches, locations, findBatch, loadLocations }) => {
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      findBatch(id);
      loadLocations();
    }
  }, [id, findBatch, loadLocations]);

  const detail = batches.activeBatch;
  const batch = detail?.batch;
  const totes = detail?.totes || [];
  const orderShipments = detail?.order_shipments || [];
  const assignedUser = detail?.assigned_user;
  const items = batch?.items || [];
  const returns = batch?.return_items || [];

  const locationLabel = (locationId) => {
    const match = (locations.locations || []).find((location) => String(location.id) === String(locationId));
    return match?.label || (locationId ? `#${locationId}` : '-');
  }

  return (
    <SettingsPage
      title={batch ? (batch.name || 'Batch') : 'Batch'}
      description="Have questions or need some help? Contact us at support@athousandapps.com"
      backAction={{ content: 'Batches', onAction: () => window.history.back() }}
    >

      { batches.isFinding &&
        <Card><LoadingSpinner size="small" statusText="Loading batch"/></Card>
      }

      { batches.errors && !batches.isFinding &&
        <Banner
          title="Batch request failed"
          tone="critical"
        >
          <p>{batches.errors}</p>
        </Banner>
      }

      { batch &&
        <>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" wrap>
                <InlineStack gap="300" blockAlign="center">
                  <Text as="h2" variant="headingMd">{batch.name || batch.id}</Text>
                  <Badge tone={STATUS_BADGE_TONES[batch.status] || 'info'}>{STATUS_LABELS[batch.status] || batch.status}</Badge>
                  <Button
                    variant="plain"
                    icon={RefreshIcon}
                    loading={batches.isFinding}
                    onClick={() => findBatch(id)}
                  >
                    Refresh
                  </Button>
                </InlineStack>
                <BlockStack gap="100">
                  <Text as="p">Totes: {batch.tote_count}</Text>
                  <Text as="p">Items: {batch.item_count}</Text>
                  <Text as="p">Cart grid: {batch.total_rows} rows x {batch.total_columns} columns</Text>
                  <Text as="p">Cart: {batch.cart_id || 'Not assigned'}</Text>
                </BlockStack>
              </InlineStack>

              <InlineStack gap="800" align="start" wrap>
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm">Status history</Text>
                  {STATUS_FIELDS.map(([field, label]) => (
                    <Text as="p" key={field}>{label}: {formatTime(batch[field])}</Text>
                  ))}
                  {(batch.picking_parks || []).map((park, index) => (
                    <Text as="p" key={`picking_park_${index}`}>Picking parked: {formatPark(park)}</Text>
                  ))}
                  {(batch.packing_parks || []).map((park, index) => (
                    <Text as="p" key={`packing_park_${index}`}>Packing parked: {formatPark(park)}</Text>
                  ))}
                  {(batch.returning_parks || []).map((park, index) => (
                    <Text as="p" key={`returning_park_${index}`}>Returning parked: {formatPark(park)}</Text>
                  ))}
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm">Assignment</Text>
                  <Text as="p">Assigned user: {assignedUser ? `${assignedUser.login_name || assignedUser.email} (${assignedUser.email})` : (batch.assigned_user_email || 'Unassigned')}</Text>
                  {batch.returner_email && <Text as="p">Returner: {batch.returner_email}</Text>}
                  <Text as="p">Created: {formatTime(batch.created_at)}</Text>
                  <Text as="p">Due: {formatTime(batch.due_date)}</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Order Shipments</Text>
              {orderShipments.map((shipment) => (
                <Box
                  key={shipment.order_shipment_id}
                  background="bg-surface-secondary"
                  borderColor="border"
                  borderWidth="025"
                  borderRadius="200"
                  padding="300"
                >
                  <InlineStack gap="800" align="start" wrap>
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingSm">Order #{shipment.order_id} / Shipment #{shipment.shipment_id}</Text>
                      <Text as="p">Status: {shipment.status || '-'}</Text>
                      <Text as="p">Shipping method: {shipment.shipping_method || '-'}</Text>
                      {shipment.order_comments &&
                        <Text as="p">Order comments: {shipment.order_comments}</Text>
                      }
                      {shipment.staff_notes &&
                        <Text as="p">Staff notes: {shipment.staff_notes}</Text>
                      }
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingSm">Shipping Address</Text>
                      {formatAddress(shipment.shipping_address).map((line, i) => <Text as="p" key={i}>{line}</Text>)}
                    </BlockStack>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Totes</Text>
              <IndexTable
                resourceName={{ singular: 'tote', plural: 'totes' }}
                itemCount={totes.length}
                selectable={false}
                headings={[
                  { title: 'Tote' },
                  { title: 'Position' },
                  { title: 'Location' },
                  { title: 'Order' },
                  { title: 'Shipment' },
                  { title: 'Items' },
                ]}
              >
                {totes.map((tote, index) => (
                  <IndexTable.Row id={tote.id || tote.sort_key} key={tote.id || tote.sort_key} position={index}>
                    <IndexTable.Cell>{tote.position_label || (tote.id || '').slice(-8)}</IndexTable.Cell>
                    <IndexTable.Cell><PositionGrid rows={batch.total_rows} columns={batch.total_columns} row={tote.row} column={tote.column} /></IndexTable.Cell>
                    <IndexTable.Cell>{locationLabel(tote.location_id)}</IndexTable.Cell>
                    <IndexTable.Cell>{`#${tote.order_id}`}</IndexTable.Cell>
                    <IndexTable.Cell>{`#${tote.shipment_id}`}</IndexTable.Cell>
                    <IndexTable.Cell>{`${(tote.items || []).length} lines / ${(tote.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0)} units`}</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
          </Card>

          {returns.length > 0 &&
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Remaining Items (Returns)</Text>
                <Text as="p">Picked items still on the cart - awaiting return to stock or set aside.</Text>
                {returns.map((entry) => (
                  <Box
                    key={entry.tote_id}
                    background="bg-surface-secondary"
                    borderColor="border"
                    borderWidth="025"
                    borderRadius="200"
                    padding="300"
                  >
                    <InlineStack gap="300" blockAlign="center">
                      <Text as="h3" variant="headingSm">{entry.position_label || `Tote ${(entry.tote_id || '').slice(-8)}`}</Text>
                      <PositionGrid rows={batch.total_rows} columns={batch.total_columns} row={entry.row} column={entry.column} color={UI_COLORS.accent} />
                    </InlineStack>
                    <Text as="p">Shipment #{entry.order_shipment_id}</Text>
                    <IndexTable
                      resourceName={{ singular: 'item', plural: 'items' }}
                      itemCount={(entry.items || []).length}
                      selectable={false}
                      headings={[
                        { title: 'Item' },
                        { title: 'SKU' },
                        { title: 'Qty' },
                      ]}
                    >
                      {(entry.items || []).map((item, index) => (
                        <IndexTable.Row id={`${entry.tote_id}-${index}`} key={index} position={index}>
                          <IndexTable.Cell>{item.name}</IndexTable.Cell>
                          <IndexTable.Cell>{item.sku || '-'}</IndexTable.Cell>
                          <IndexTable.Cell>{item.quantity}</IndexTable.Cell>
                        </IndexTable.Row>
                      ))}
                    </IndexTable>
                  </Box>
                ))}
              </BlockStack>
            </Card>
          }

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Items</Text>
              <IndexTable
                resourceName={{ singular: 'item', plural: 'items' }}
                itemCount={items.length}
                selectable={false}
                headings={[
                  { title: 'Item' },
                  { title: 'SKU' },
                  { title: 'UPC' },
                  { title: 'BPN' },
                  { title: 'Qty' },
                  { title: 'Tote' },
                  { title: 'Position' },
                  { title: 'Order' },
                ]}
              >
                {items.map((item, index) => (
                  <IndexTable.Row id={`${item.toteId}-${index}`} key={index} position={index}>
                    <IndexTable.Cell>{item.name}</IndexTable.Cell>
                    <IndexTable.Cell>{item.sku || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.upc || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.bpn || '-'}</IndexTable.Cell>
                    <IndexTable.Cell>{item.quantity}</IndexTable.Cell>
                    <IndexTable.Cell>{(item.toteId || '').slice(-8)}</IndexTable.Cell>
                    <IndexTable.Cell><PositionGrid rows={batch.total_rows} columns={batch.total_columns} row={item.row} column={item.column} /></IndexTable.Cell>
                    <IndexTable.Cell>{`#${item.orderShipmentId}`}</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
          </Card>

          <Card>
            <Link to="/batches">Back to Active Batches</Link>
          </Card>
        </>
      }
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  batches: state.batches,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  findBatch: (batchId) => dispatch(batchFind(batchId)),
  loadLocations: () => dispatch(locationsIndex()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BatchDetail);
